import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import EventFilters from "@/components/EventFilters";
import { Calendar, MapPin, Users, DollarSign, Clock, Trophy, Image, Eye } from "lucide-react";

const FindGamesSection = ({ events: initialEvents, onRSVP, token }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState(initialEvents || []);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    game_types: [],
    skill_level: "all",
    date_from: "",
    date_to: "",
    city: "",
    min_fee: undefined,
    max_fee: undefined,
    has_spots: false,
    sort_by: "date_asc"
  });

  // Update events when initialEvents changes
  useEffect(() => {
    if (initialEvents && initialEvents.length > 0) {
      setEvents(initialEvents);
    }
  }, [initialEvents]);

  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      
      if (filters.search) params.append("q", filters.search);
      if (filters.game_types?.length > 0) params.append("game_types", filters.game_types.join(","));
      if (filters.skill_level && filters.skill_level !== "all") params.append("skill_level", filters.skill_level);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (filters.city) params.append("city", filters.city);
      if (filters.min_fee !== undefined) params.append("min_fee", filters.min_fee);
      if (filters.max_fee !== undefined) params.append("max_fee", filters.max_fee);
      if (filters.has_spots) params.append("has_spots", "true");
      if (filters.sort_by) params.append("sort_by", filters.sort_by);

      const response = await axios.get(`${API}/events/search?${params.toString()}`);
      setEvents(response.data.events || []);
      
      const count = response.data.count || 0;
      toast.success(`Found ${count} event${count !== 1 ? "s" : ""}`);
    } catch (err) {
      console.error("Error searching events:", err);
      toast.error("Failed to search events");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      game_types: [],
      skill_level: "all",
      date_from: "",
      date_to: "",
      city: "",
      min_fee: undefined,
      max_fee: undefined,
      has_spots: false,
      sort_by: "date_asc"
    });
    // Reset to initial events
    if (initialEvents) {
      setEvents(initialEvents);
    }
    toast.success("Filters cleared");
  };

  return (
    <div data-testid="find-games-section">
      {/* Section Header */}
      <div className="mb-12">
        <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
          DISCOVER YOUR NEXT GAMING CHALLENGE
        </h2>
        <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg max-w-3xl">
          Board2Boston™ connects you with gaming events in your area every week. From casual kitchen table games to high-stakes tournaments, find the perfect match for your skill level and schedule.
        </p>
      </div>

      {/* Advanced Filters */}
      <EventFilters 
        filters={filters} 
        setFilters={setFilters} 
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-[#7a6200] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-[#A1A1AA] font-ubuntu mt-4">Searching events...</p>
        </div>
      )}

      {/* Events Grid */}
      {!loading && events.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#52525B] font-ubuntu text-lg">No events found matching your criteria.</p>
          <p className="text-[#A1A1AA] font-ubuntu mt-2">Try adjusting your filters or check back later.</p>
        </div>
      ) : !loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Card 
              key={event.id} 
              className="bg-[#0A0A0A] border border-white/5 hover:border-[#7a6200]/30 transition-colors overflow-hidden group"
              data-testid={`event-card-${event.id}`}
            >
              <CardContent className="p-0">
                {/* Event Header */}
                <div className="p-6 border-b border-white/5">
                  <div className="flex items-start justify-between mb-4">
                    <Badge 
                      className={`font-ubuntu text-xs ${
                        event.is_tournament 
                          ? "bg-[#7a6200]/20 text-[#C6A300] border-[#7a6200]" 
                          : "bg-white/10 text-white border-white/20"
                      }`}
                    >
                      {event.is_tournament ? (
                        <><Trophy className="w-3 h-3 mr-1" /> Tournament</>
                      ) : (
                        event.game_type
                      )}
                    </Badge>
                    <span className={`text-xs font-ubuntu capitalize px-2 py-1 rounded ${
                      event.skill_level === "advanced" ? "bg-red-500/20 text-red-400" :
                      event.skill_level === "intermediate" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-green-500/20 text-green-400"
                    }`}>
                      {event.skill_level === "all" ? "All Levels" : event.skill_level}
                    </span>
                  </div>

                  <h3 className="font-bebas text-2xl tracking-wider text-white group-hover:text-[#7a6200] transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-[#52525B] font-ubuntu text-sm mt-2 line-clamp-2">
                    {event.description}
                  </p>
                </div>

                {/* Event Details */}
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3 text-[#A1A1AA]">
                    <Calendar className="w-4 h-4 text-[#7a6200]" />
                    <span className="font-ubuntu text-sm">
                      {new Date(event.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                    <Clock className="w-4 h-4 text-[#7a6200] ml-2" />
                    <span className="font-ubuntu text-sm">{event.time}</span>
                  </div>

                  <div className="flex items-center gap-3 text-[#A1A1AA]">
                    <MapPin className="w-4 h-4 text-[#7a6200]" />
                    <span className="font-ubuntu text-sm truncate">{event.location}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-[#A1A1AA]">
                      <Users className="w-4 h-4 text-[#7a6200]" />
                      <span className="font-ubuntu text-sm">
                        {event.current_participants}/{event.max_participants} spots
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {event.entry_fee > 0 && (
                        <div className="flex items-center gap-1 text-[#C6A300]">
                          <DollarSign className="w-4 h-4" />
                          <span className="font-bebas text-lg">{event.entry_fee}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* RSVP Button */}
                <div className="px-6 pb-6 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="flex-1 border-white/20 text-[#A1A1AA] hover:text-white hover:bg-white/5"
                    data-testid={`view-btn-${event.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                  <Button
                    onClick={() => onRSVP(event.id)}
                    className="btn-gold flex-1"
                    disabled={event.current_participants >= event.max_participants}
                    data-testid={`rsvp-btn-${event.id}`}
                  >
                    {event.current_participants >= event.max_participants ? "FULL" : "RSVP"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Categories Info */}
      <div className="mt-16">
        <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-8">GAME CATEGORIES</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h4 className="font-bebas text-xl tracking-wider text-white mb-4">CARD GAMES</h4>
            <ul className="space-y-3 text-[#A1A1AA] font-ubuntu">
              <li><span className="text-[#7a6200]">Spades</span> — Our most popular game with daily events</li>
              <li><span className="text-[#7a6200]">Whist</span> — Traditional and modern variations</li>
              <li><span className="text-[#7a6200]">Bridge</span> — Competitive and social games</li>
              <li><span className="text-[#7a6200]">Other</span> — Poker, Hearts, Pinochle, and more</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bebas text-xl tracking-wider text-white mb-4">BOARD GAMES</h4>
            <ul className="space-y-3 text-[#A1A1AA] font-ubuntu">
              <li><span className="text-[#7a6200]">Backgammon</span> — Ancient strategy meets modern play</li>
              <li><span className="text-[#7a6200]">Checkers</span> — American and International Draughts</li>
              <li><span className="text-[#7a6200]">Chess</span> — Rated and unrated games</li>
              <li><span className="text-[#7a6200]">Modern</span> — Monopoly, Settlers of Catan, and more</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindGamesSection;
